import { type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { Pill, type PillTone } from "@/components/ui/pill";
import { thresholdTone } from "@/features/oee/oee-thresholds";
import { useOeeThresholds } from "@/hooks/useOrganizationPolicy";
import { OverrunBadge, isOverrun } from "@/features/oee/OverrunBadge";
import { dueLabel, formatPct, hmTime, lateLabel } from "./production-order-utils";

// Partes compartilhadas dos indicadores por OP (RUNNING parcial + COMPLETED). Extraídas p/ manter o
// orquestrador `ProductionOrderIndicators` ≤200 LOC (review agendamento p1).
export const pct = formatPct;
export const minLabel = (m: number | null | undefined) =>
  m == null ? "—" : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
// HH:mm do horário previsto de fim (vem do backend; não derivamos ETA no cliente). Reusa o hmTime
// canônico da feature (fonte única do formato de horário).
export const clockLabel = (iso: string | null | undefined) => (iso ? hmTime(iso) : "—");

const TONE_TEXT: Record<PillTone, string> = {
  success: "text-rd-success-700",
  warning: "text-rd-warning-700",
  danger: "text-rd-danger-700",
  info: "text-rd-info-600",
  brand: "text-brand-700",
  neutral: "text-text-primary",
};

export function Bar({
  letter,
  label,
  value,
  fill,
}: {
  letter: string;
  label: string;
  value: number | null;
  fill: string;
}) {
  // A Disponibilidade perdeu o teto semântico de 100% (o Desempenho nunca teve), então
  // `--p` precisa ser CLAMPADO: sem isso um excedente vira uma div mais larga que a pista, o
  // `overflow-hidden` engole a diferença e 250% desenha idêntico a 100%. Clampar sem marcar, porém,
  // é a metade errada do conserto — o mesmo primitivo das demais telas marca o excedente
  // em pontos, e a ponta direita quadrada diz "continua além da pista".
  const over = isOverrun(value);
  const p = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-3 font-mono font-bold text-text-tertiary">{letter}</span>
      <span className="w-24 text-text-secondary">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-bg-sunken">
        <div
          style={{ "--p": `${p}%` } as CSSProperties}
          className={cn("h-full w-[var(--p)] rounded-full", fill, over && "rounded-r-none")}
        />
      </div>
      <span className="inline-flex w-[86px] shrink-0 items-center justify-end gap-1 whitespace-nowrap font-mono tabular-nums">
        {pct(value)}
        <OverrunBadge value={value} metricLabel={label} />
      </span>
    </div>
  );
}

export function Tile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: string;
}) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className={cn("mt-1 font-mono text-[22px] font-medium tabular-nums", tone)}>{value}</div>
      {sub ? <div className="text-[11px] text-text-tertiary">{sub}</div> : null}
    </div>
  );
}

export interface TileSpec {
  label: string;
  value: string;
  sub?: string;
  tone?: string;
}

// Grade de tiles — fonte única do wrapper (RUNNING e COMPLETED só montam o array). Evita o
// copy-paste das duas grades (code review p7).
export function TileGrid({ tiles }: { tiles: TileSpec[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {tiles.map((t) => (
        <Tile key={t.label} {...t} />
      ))}
    </div>
  );
}

// Tiles partilhados entre os estados (eram idênticos/quase-idênticos nas duas grades).
export const downtimeTile = (downtimeMin: number | null): TileSpec => ({
  label: "Downtime",
  value: downtimeMin != null ? `${downtimeMin} min` : "—",
  sub: "paradas na janela",
});
export const scrapTile = (scrapPct: number | null): TileSpec => ({
  label: "Refugo",
  value: pct(scrapPct),
  sub: "inspeção",
  tone: "text-rd-warning-700",
});
// Prazo do cliente nos indicadores. `kind` decide o SUBTÍTULO: projeção (o fim
// planejado ainda pode mudar) vs fato consumado. Sem prazo o tile some — um "—" grande sugeriria
// dado faltando, quando na verdade não existe compromisso a cobrar.
export const dueTile = (due?: {
  dueDate: string | null;
  latenessMin: number | null;
  kind: "PROJECTED" | "ACTUAL" | null;
}): TileSpec | null => {
  // `due?` é cinto-e-suspensórios: o contrato o exige, mas uma resposta antiga em cache não pode
  // derrubar o painel inteiro de indicadores por causa de um tile.
  if (!due?.dueDate || due.latenessMin == null) return null;
  const late = due.latenessMin > 0;
  return {
    label: due.kind === "ACTUAL" ? "Atraso vs prazo" : "Atraso projetado",
    value: late ? lateLabel(due.latenessMin) : "No prazo",
    sub: `prazo ${dueLabel(due.dueDate)}`,
    tone: late ? "text-rd-danger-700" : "text-rd-success-700",
  };
};

export const delayTile = (startDelayMin: number | null, tone?: string): TileSpec => ({
  label: "Atraso no início",
  value: startDelayMin != null ? `+${startDelayMin} min` : "—",
  sub: "vs planejado",
  tone,
});

// Hero OEE + barras A/P/Q. `live` = run em andamento (OEE PARCIAL recalculado sobre o tempo já
// decorrido): muda o rótulo/cor e o degradê; no COMPLETED mantém o veredito de meta.
export function OeeHero({
  oee,
  availability,
  performance,
  quality,
  live,
}: {
  oee: number | null;
  availability: number | null;
  performance: number | null;
  quality: number | null;
  live?: boolean;
}) {
  // Hook em variável própria: chamá-lo dentro do argumento funciona, mas esconde uma chamada de
  // hook num lugar onde ninguém a procura.
  const thresholds = useOeeThresholds();
  const tone = thresholdTone(oee, thresholds);
  return (
    <div
      className={cn(
        "grid grid-cols-[auto_1fr] gap-4 rounded-lg border bg-gradient-to-b to-transparent p-4",
        live ? "from-rd-info-50" : "from-rd-success-50"
      )}
    >
      <div>
        <div className="text-[11px] uppercase tracking-wide text-text-tertiary">
          {live ? "OEE parcial · ao vivo" : "OEE · janela real"}
        </div>
        <div className="flex items-end gap-1">
          <span
            className={cn(
              "font-mono text-5xl font-medium tabular-nums leading-none",
              TONE_TEXT[tone]
            )}
          >
            {oee?.toFixed(1) ?? "—"}
          </span>
          <span className="pb-1 text-xl text-text-tertiary">%</span>
        </div>
        {oee != null ? (
          <Pill tone={live ? "info" : tone} className="mt-2">
            {/* Spec 079 — a META é o MESMO limiar que pinta o número acima. Com o `85` escrito à
                mão aqui, uma fábrica com verde em 90 lia "✓ Acima da meta (85%)" num crachá
                AMARELO: o texto contradizendo a cor, no mesmo componente. */}
            {live
              ? "parcial — recalcula ao vivo"
              : oee >= thresholds.good
                ? `✓ Acima da meta (${thresholds.good}%)`
                : `Abaixo da meta (${thresholds.good}%)`}
          </Pill>
        ) : null}
      </div>
      <div className="flex flex-col justify-center gap-2 border-l pl-4">
        <Bar letter="A" label="Disponibilidade" value={availability} fill="bg-rd-success-500" />
        <Bar letter="P" label="Desempenho" value={performance} fill="bg-rd-info-500" />
        <Bar letter="Q" label="Qualidade" value={quality} fill="bg-brand-500" />
      </div>
    </div>
  );
}
